<template>
  <v-container fluid>
    <v-row v-if="isLoading">
      <v-col cols="12">
        <loading-spinner></loading-spinner>
      </v-col>
    </v-row>
    <v-row v-else>
      <v-col cols="12">
        <v-card>
          <card-header :title="$t('title.agreement')"></card-header>

          <v-card-text>
            <v-form ref="form" v-model="valid">
              <v-row>
                <v-col
                  cols="12"
                  v-for="(document, index) in journal?.consent_documents"
                  :key="index"
                >
                  <label class="text-subtitle-1">{{
                    document.document_type_name
                  }}</label>
                  <v-textarea
                    class="text-body-2"
                    v-model="document.content"
                    :disabled="true"
                    outlined
                  ></v-textarea>

                  <div class="d-flex justify-end pa-0 ma-0">
                    <v-radio-group
                      v-model="document.consent"
                      class="pa-0 ma-0"
                      row
                      dense
                      :rules="[rules.required]"
                      @change="handleConsentChange(document.idx, $event)"
                    >
                      <v-radio
                        :label="$t('label.agreement')"
                        :value="true"
                        color="primary"
                      ></v-radio>
                      <v-radio
                        :label="$t('label.disagreement')"
                        :value="false"
                        color="error"
                      ></v-radio>
                    </v-radio-group>
                  </div>
                </v-col>

                <v-text-field
                  :label="$t('label.signature')"
                  v-model="signature.content"
                  :placeholder="$t('txt.signature_placeholder')"
                  class="ml-5 mr-5"
                  type="text"
                  :rules="[rules.required]"
                ></v-text-field>
              </v-row>
            </v-form>
          </v-card-text>

          <v-card-actions class="pa-5">
            <custom-button
              :label="$t('button.close')"
              @click="toPaperManagement"
              rounded
              color="black"
              gradient
            ></custom-button>
            <v-spacer></v-spacer>
            <custom-button
              :label="$t('button.next')"
              rounded
              gradient
              color="primary"
              :disabled="!valid"
              @click="handleSave"
            ></custom-button>
          </v-card-actions>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import LoadingSpinner from "@/components/common/Spinners/LoadingSpinner.vue";
import CardHeader from "@/components/cards/CardHeader.vue";
import CustomButton from "@/components/common/buttons/CustomButton.vue";

import { savePopup } from "@/fields/paper/popups";

import { paperApi, journalApi } from "@/api/api";

export default {
  name: "submit-agreement",
  components: {
    CardHeader,
    LoadingSpinner,
    CustomButton,
  },
  props: {
    journalIdx: {
      type: [Number, String],
      required: true,
    },
  },
  data() {
    return {
      journal: null,
      signature: {
        consents: [],
        content: null,
      },
      valid: false,
      rules: {
        required: (v) => !!v || "This field is required",
      },
      isLoading: false,
    };
  },
  methods: {
    toPaperManagement() {
      this.$router.push({
        name: "select-journal",
      });
    },
    toSubmissions(paperIdx) {
      this.$router.push({
        name: "submission-edit",
        params: {
          idx: paperIdx,
        },
      });
    },
    handleConsentChange(idx, consent) {
      // 이미 있는 동의 정보인지 확인
      const existingIndex = this.signature.consents.findIndex(
        (c) => c.consent_document_idx === idx
      );

      if (existingIndex !== -1) {
        // 이미 있으면 업데이트
        this.signature.consents[existingIndex].consent = consent;
      } else {
        // 없으면 새로 추가
        this.signature.consents.push({
          consent_document_idx: idx,
          consent: consent,
        });
      }
    },
    async fetchConsentDocument() {
      try {
        this.isLoading = true;
        const response = await journalApi.showJournalForAgreement({
          idx: this.journalIdx,
        });
        if (response && response.status === 200) {
          this.journal = {
            ...response.data.response,
            consent_documents: response.data.response?.consent_documents.map(
              (doc) => ({
                ...doc,
                consent: null,
              })
            ),
          };
        }
      } catch (error) {
        this.notifyVue({
          message: this.$t("alert.failed_request"),
          style: "danger",
        });
      } finally {
        this.isLoading = false;
      }
    },
    async savePaper() {
      const data = {
        journal_idx: this.journalIdx,
        signature: this.signature,
      };
      try {
        const response = await paperApi.createPaper(data);
        if (response && response.status === 201) {
          const paperIdx = response.data.response.paper_idx;
          if (paperIdx) {
            this.toSubmissions(paperIdx);
          } else {
            this.notifyVue({
              message: this.$t("alert.failed_request"),
              style: "danger",
            });
          }
        }
      } catch (error) {
        this.notifyVue({
          message: this.$t("alert.failed_request"),
          style: "danger",
        });
      }
    },
    async handleSave() {
      // 모든 문서에 대한 동의 여부 확인
      const allConsents = this.journal.consent_documents.every(
        (doc) => doc.consent === true
      );

      if (!allConsents) {
        this.notifyVue({
          message: this.$t("alert.please_agree_all_documents"),
          style: "warning",
        });
        return;
      }

      const isConfirmed = await savePopup();
      if (isConfirmed) {
        await this.savePaper();
      }
    },

    /* ****************************************************************************************
     * 알럿
     * ****************************************************************************************/
    notifyVue({
      vertical = "top",
      horizontal = "right",
      message = "",
      style = "primary",
      icon = "priority_high",
    } = {}) {
      this.$notify({
        timeout: 8000,
        message: message,
        icon: icon,
        type: style,
        horizontalAlign: horizontal,
        verticalAlign: vertical,
      });
    },
  },
  mounted() {
    this.fetchConsentDocument();
  },
};
</script>

<style scoped></style>
